// coffee.js — bean-aware coffee dose & grind calculator.
// Tuned specifically for Fellow Aiden brewer + Fellow Ode Gen 2 grinder.
//
// Produces a *full* Aiden profile object — every parameter the brewer's
// settings expose (bloom ratio/duration/temp, single-serve and batch pulse
// counts/intervals/per-pulse temps). The shape matches what the
// reverse-engineered Fellow API expects, so the same object can be rendered
// in the UI today and pushed to the brewer later via aiden.js.

export const ROAST_LEVELS = [
  { id: "light", label: "Light", color: "#D4B896" },
  { id: "medium-light", label: "Medium-Light", color: "#B8956A" },
  { id: "medium", label: "Medium", color: "#8B6F47" },
  { id: "medium-dark", label: "Medium-Dark", color: "#5C4530" },
  { id: "dark", label: "Dark", color: "#3D2D1F" },
];

export const PROCESS_TYPES = [
  { id: "washed", label: "Washed", note: "Clean acidity, balanced" },
  { id: "natural", label: "Natural", note: "Fruity, sometimes funky" },
  { id: "honey", label: "Honey", note: "Sweet, balanced" },
  { id: "anaerobic", label: "Anaerobic", note: "Intense, often funky" },
  { id: "wet-hulled", label: "Wet-Hulled", note: "Earthy, full-bodied" },
];

// Aiden brew profiles. Each carries the *full* parameter set the brewer accepts.
// profileType: 0 = pour-over (hot), 1 = cold brew.
// Per-pulse temperature arrays let you ramp temps across pulses; length must
// match pulse count. Single-value arrays hold for every pulse.
export const AIDEN_PROFILES = {
  "fellows-classic": {
    label: "Fellow's Classic",
    description: "Balanced, the default starting point",
    profileType: 0,
    ratio: 16.5,
    bloomEnabled: true,
    bloomRatio: 2.5,
    bloomDuration: 45,
    bloomTemperature: 96,
    ssPulsesEnabled: true,
    ssPulsesNumber: 3,
    ssPulsesInterval: 23,
    ssPulseTemperatures: [96, 96, 96],
    batchPulsesEnabled: true,
    batchPulsesNumber: 3,
    batchPulsesInterval: 30,
    batchPulseTemperatures: [96, 96, 96],
  },
  "bold": {
    label: "Bold",
    description: "Heavier body, more concentration",
    profileType: 0,
    ratio: 15,
    bloomEnabled: true,
    bloomRatio: 2,
    bloomDuration: 45,
    bloomTemperature: 96,
    ssPulsesEnabled: true,
    ssPulsesNumber: 3,
    ssPulsesInterval: 25,
    ssPulseTemperatures: [96, 96, 96],
    batchPulsesEnabled: true,
    batchPulsesNumber: 3,
    batchPulsesInterval: 32,
    batchPulseTemperatures: [96, 96, 96],
  },
  "balanced": {
    label: "Balanced",
    description: "Wide-window everyday brew",
    profileType: 0,
    ratio: 16,
    bloomEnabled: true,
    bloomRatio: 2.5,
    bloomDuration: 40,
    bloomTemperature: 95,
    ssPulsesEnabled: true,
    ssPulsesNumber: 3,
    ssPulsesInterval: 22,
    ssPulseTemperatures: [95, 95, 95],
    batchPulsesEnabled: true,
    batchPulsesNumber: 3,
    batchPulsesInterval: 28,
    batchPulseTemperatures: [95, 95, 95],
  },
  "light-roast": {
    label: "Light Roast Special",
    description: "Higher temp, longer bloom, ramped pulses for clarity",
    profileType: 0,
    ratio: 16.5,
    bloomEnabled: true,
    bloomRatio: 3,
    bloomDuration: 60,
    bloomTemperature: 98,
    ssPulsesEnabled: true,
    ssPulsesNumber: 4,
    ssPulsesInterval: 22,
    ssPulseTemperatures: [96, 97, 98, 98],
    batchPulsesEnabled: true,
    batchPulsesNumber: 4,
    batchPulsesInterval: 28,
    batchPulseTemperatures: [96, 97, 98, 98],
  },
  "iced": {
    label: "Iced (Japanese-style)",
    description: "Brewed hot over ice; uses less water",
    profileType: 0,
    ratio: 11,
    bloomEnabled: true,
    bloomRatio: 2.5,
    bloomDuration: 45,
    bloomTemperature: 96,
    ssPulsesEnabled: true,
    ssPulsesNumber: 3,
    ssPulsesInterval: 23,
    ssPulseTemperatures: [96, 96, 96],
    batchPulsesEnabled: true,
    batchPulsesNumber: 3,
    batchPulsesInterval: 30,
    batchPulseTemperatures: [96, 96, 96],
    note: "Add ~40% of yield as ice in carafe before brewing",
  },
  "cold-brew": {
    label: "Cold Brew",
    description: "Long slow extraction, no heat",
    profileType: 1,
    // 1:14 is the handoff's ready-to-drink baseline (80–85 g / 8 cups). The old
    // 1:8 was concentrate-strength — Josh explicitly wants "no concentrate",
    // and it silently doubled the displayed dose (150 g for 8 cups).
    // Bloom stays disabled in the PUSHED profile: the Fellow API contract for
    // cold+bloom is unverified, and a rejected push would break the morning.
    ratio: 14,
    bloomEnabled: false,
    bloomRatio: 0,
    bloomDuration: 0,
    bloomTemperature: 0,
    ssPulsesEnabled: false,
    ssPulsesNumber: 0,
    ssPulsesInterval: 0,
    ssPulseTemperatures: [],
    batchPulsesEnabled: false,
    batchPulsesNumber: 0,
    batchPulsesInterval: 0,
    batchPulseTemperatures: [],
    note: "Cold brew runs the brewer's automated cold mode",
  },
};

// Ode Gen 2 grind setting recommendations by brew style.
// 1 = finest, 11 = coarsest (Ode Gen 2 numbered detents).
// Canonical Ode Gen 2 settings from Josh's handoff: hot brews live ~5.3–5.6,
// cold brew ~8.2 (coarser). (Superseded the stale ~8.1 "favorite".)
export const ODE_GRIND_GUIDE = {
  "fellows-classic": { setting_range: [5.3, 5.6], default: 5.4, descriptor: "medium-fine" },
  "bold":            { setting_range: [5.3, 5.6], default: 5.3, descriptor: "medium-fine" },
  "balanced":        { setting_range: [5.3, 5.6], default: 5.4, descriptor: "medium-fine" },
  "light-roast":     { setting_range: [5.6, 6.2], default: 5.6, descriptor: "medium-fine" },
  "iced":            { setting_range: [5.3, 5.6], default: 5.4, descriptor: "medium-fine" },
  "cold-brew":       { setting_range: [7.9, 8.5], default: 8.2, descriptor: "coarse" },
};

// Roast → grind offset, in Ode clicks. NOT a linear ramp: both ends go COARSER.
//
// Light/bright coffees are prone to sharp acidity and over-extraction, so they
// get coarser (docs/coffee-handoff.md → bright band 5.6–6.2). Dark roasts go
// coarser too, for the opposite reason — to tame bitterness. Medium and
// medium-dark sit at the baseline band, 5.3–5.6.
//
// This reverses the older linear ramp (light −1 → dark +1), which ground light
// roasts finer per conventional wisdom. That contradicted both the handoff and
// the standing bias toward lower extraction and a smoother, sweeter cup.
//
// Magnitudes are deliberately small. The whole hot band is 0.3 clicks wide and
// the handoff describes real corrections as 0.2–0.4, so the old ±1 offsets were
// an order of magnitude too coarse for the scale they operated on.
const ROAST_GRIND_OFFSET  = { "light": +0.4, "medium-light": +0.2, "medium": 0, "medium-dark": 0, "dark": +0.3 };
const ROAST_TEMP_OFFSET   = { "light": +2,   "medium-light": +1,   "medium": 0, "medium-dark": -1,   "dark": -2 };
const ROAST_RATIO_OFFSET  = { "light": +0.5, "medium-light": +0.25,"medium": 0, "medium-dark": -0.25,"dark": -0.5 };

/**
 * Compute the full Aiden brew object for a given bean + base profile + water target.
 * Returns a denormalized result with everything the UI needs to display, plus an
 * `aidenProfile` field shaped exactly like the Fellow API's profile schema.
 */
export function calculateAidenBrew({
  bean, profileId = "balanced", waterMl = 500, profileTitle = null, ratioOverride = null,
  // Grinder scale from lib/coffee-gear.js. Omitted means the Fellow Ode, which
  // is what every caller assumed before this parameter existed — so existing
  // behaviour is unchanged unless a caller passes a different grinder.
  grinder = null,
}) {
  const base = AIDEN_PROFILES[profileId] || AIDEN_PROFILES.balanced;
  const roast = bean?.roast_level || "medium";

  // ratioOverride lets the user log a one-off brew at a custom ratio without
  // creating a new Aiden profile — bypasses both the profile's base ratio
  // and the roast-bias offset.
  const ratio = ratioOverride && ratioOverride > 0
    ? +(+ratioOverride).toFixed(2)
    : +(base.ratio + (ROAST_RATIO_OFFSET[roast] || 0)).toFixed(2);
  const tempOffset = ROAST_TEMP_OFFSET[roast] || 0;

  const dose = +(waterMl / ratio).toFixed(1);
  const bloomWater = +(dose * base.bloomRatio).toFixed(0);

  const bloomTemp = base.bloomEnabled ? clampTemp(base.bloomTemperature + tempOffset) : 0;
  const ssPulseTemps    = base.ssPulseTemperatures.map(t => clampTemp(t + tempOffset));
  const batchPulseTemps = base.batchPulseTemperatures.map(t => clampTemp(t + tempOffset));

  const grind = computeGrindSetting(profileId, roast, grinder);
  const freshness = freshnessNote(bean?.roast_date);
  const processHint = processHints[bean?.process] || null;

  const displayTempC = ssPulseTemps.length
    ? Math.max(...ssPulseTemps)
    : (bloomTemp || base.bloomTemperature || 0);

  // The Aiden profile object — matches the Fellow API schema 1:1.
  const aidenProfile = {
    profileType: base.profileType,
    title: profileTitle || synthProfileTitle(bean, profileId),
    ratio,
    bloomEnabled: base.bloomEnabled,
    bloomRatio: base.bloomRatio,
    bloomDuration: base.bloomDuration,
    bloomTemperature: bloomTemp,
    ssPulsesEnabled: base.ssPulsesEnabled,
    ssPulsesNumber: base.ssPulsesNumber,
    ssPulsesInterval: base.ssPulsesInterval,
    ssPulseTemperatures: ssPulseTemps,
    batchPulsesEnabled: base.batchPulsesEnabled,
    batchPulsesNumber: base.batchPulsesNumber,
    batchPulsesInterval: base.batchPulsesInterval,
    batchPulseTemperatures: batchPulseTemps,
  };

  return {
    bean_id: bean?.id || null,
    bean_name: bean?.name || "Unknown bean",
    profile: { id: profileId, label: base.label, description: base.description },
    dose_g: dose,
    water_g: waterMl,
    bloom_water_g: bloomWater,
    bloom_time_s: base.bloomDuration,
    ratio,
    ratio_str: `1:${ratio.toFixed(1)}`,
    temp_c: displayTempC,
    temp_f: +(displayTempC * 9 / 5 + 32).toFixed(0),
    pulse_count: base.ssPulsesNumber,
    grind: {
      device: "Fellow Ode Gen 2",
      setting: grind.setting,
      setting_str: `Setting ${grind.setting}`,
      descriptor: grind.descriptor,
      adjustment_note: grind.adjustment,
    },
    freshness,
    process_hint: processHint,
    note: base.note || null,
    aidenProfile,
  };
}

function clampTemp(t) {
  if (t == null) return 0;
  return Math.max(50, Math.min(99, Math.round(t)));
}

function synthProfileTitle(bean, profileId) {
  const base = AIDEN_PROFILES[profileId];
  const beanName = bean?.name || "Custom";
  const synth = `${beanName} · ${base?.label || profileId}`;
  return synth.length > 30 ? synth.slice(0, 30) : synth;
}

function computeGrindSetting(profileId, roast, grinder) {
  const guide = ODE_GRIND_GUIDE[profileId] || ODE_GRIND_GUIDE.balanced;
  const offset = ROAST_GRIND_OFFSET[roast] || 0;

  // A grinder with no numbered scale gets a descriptor and no number. Inventing
  // one would be worse than useless: "5.4" means nothing on a grinder whose
  // dial is unmarked, and the user cannot act on it.
  if (grinder && grinder.min == null) {
    return {
      setting: null,
      descriptor: guide.descriptor,
      adjustment: offset > 0 ? "A little coarser than your usual"
                : offset < 0 ? "A little finer than your usual"
                : "Your usual setting",
    };
  }

  // Default to the Ode's range — what every caller assumed before grinders were
  // configurable — then map the Ode-derived value onto the user's own scale.
  const min = grinder?.min ?? 1;
  const max = grinder?.max ?? 11;
  const step = grinder?.step ?? 0.1;

  let raw = guide.default + offset;
  if (grinder && (min !== 1 || max !== 11)) {
    // The guide's numbers are Ode-native, so proportionally remap them.
    const ratio = (raw - 1) / (11 - 1);
    raw = min + ratio * (max - min);
  }
  const decimals = step < 1 ? 1 : 0;
  const setting = +Math.max(min, Math.min(max, Math.round(raw / step) * step)).toFixed(decimals);
  let adjustment;
  if (offset > 0) {
    // Both light and dark land here — the reason differs, so say which.
    const why = (roast === "light" || roast === "medium-light")
      ? "tames acidity"
      : "tames bitterness";
    adjustment = `${offset} coarser than baseline (${why})`;
  } else if (offset < 0) {
    adjustment = `${Math.abs(offset)} finer than baseline`;
  } else {
    adjustment = "Baseline for this roast";
  }
  return { setting, descriptor: guide.descriptor, adjustment };
}

function freshnessNote(roastDate) {
  if (!roastDate) return { status: "unknown", text: "No roast date — can't assess freshness" };
  const days = Math.floor((Date.now() - new Date(roastDate).getTime()) / 86400000);
  if (days < 3)  return { status: "too-fresh", days, text: `${days}d off roast — too fresh, rest 1-2 more days` };
  if (days <= 21) return { status: "peak",     days, text: `${days}d off roast — peak window` };
  if (days <= 35) return { status: "past-peak",days, text: `${days}d off roast — past peak, may need finer grind` };
  return            { status: "stale",     days, text: `${days}d off roast — stale, flavors will be flat` };
}

const processHints = {
  washed: "Clean acidity. Highlights the bean's terroir.",
  natural: "Fruit-forward, sometimes boozy. Try the lighter ratio if too intense.",
  anaerobic: "Funky and intense. Don't be alarmed by unusual flavors.",
  honey: "Balanced sweetness, very forgiving across settings.",
  "wet-hulled": "Earthy and full-bodied. Try the bold profile.",
};

// ---------------------------------------------------------------------------
// User-edited profile overrides
// ---------------------------------------------------------------------------
// Apply user edits on top of the computed profile. The editor passes a partial
// aidenProfile and we merge — recomputing dose/bloom water from ratio + waterMl.
export function applyProfileOverrides(brew, overrides) {
  if (!overrides || !brew) return brew;
  const merged = { ...brew.aidenProfile, ...overrides };
  // Keep pulse temperature arrays in sync with their pulse-count counterpart.
  if (overrides.ssPulsesNumber != null) {
    merged.ssPulseTemperatures = padOrTrim(merged.ssPulseTemperatures, overrides.ssPulsesNumber);
  }
  if (overrides.batchPulsesNumber != null) {
    merged.batchPulseTemperatures = padOrTrim(merged.batchPulseTemperatures, overrides.batchPulsesNumber);
  }

  const dose = +(brew.water_g / merged.ratio).toFixed(1);
  const bloomWater = +(dose * merged.bloomRatio).toFixed(0);
  const headlineTemp = merged.ssPulseTemperatures?.length
    ? Math.max(...merged.ssPulseTemperatures)
    : merged.bloomTemperature;

  return {
    ...brew,
    aidenProfile: merged,
    dose_g: dose,
    bloom_water_g: bloomWater,
    ratio: merged.ratio,
    ratio_str: `1:${merged.ratio.toFixed(1)}`,
    temp_c: headlineTemp,
    temp_f: +(headlineTemp * 9 / 5 + 32).toFixed(0),
    pulse_count: merged.ssPulsesNumber,
    bloom_time_s: merged.bloomDuration,
  };
}

function padOrTrim(arr, len) {
  const a = arr?.slice() || [];
  if (a.length === len) return a;
  if (a.length > len) return a.slice(0, len);
  const last = a[a.length - 1] ?? 96;
  while (a.length < len) a.push(last);
  return a;
}

// Bounded inputs the editor uses to constrain sliders.
export const AIDEN_LIMITS = {
  ratio: { min: 10, max: 22, step: 0.5 },
  bloomRatio: { min: 1, max: 4, step: 0.5 },
  bloomDuration: { min: 0, max: 120, step: 5 },
  bloomTemperature: { min: 50, max: 99, step: 1 },
  pulsesNumber: { min: 1, max: 6, step: 1 },
  pulsesInterval: { min: 5, max: 60, step: 1 },
  pulseTemperature: { min: 50, max: 99, step: 1 },
  waterMl: { min: 150, max: 1500, step: 10 },
};

// Shift every temperature in an Aiden profile by a Fahrenheit delta, clamped to
// the machine's 50–99 °C pulse range. Used when a manual temp nudge in Morning
// Mode must move the profile actually pushed to the brewer, not just the headline
// temp — otherwise the logged temp and the pushed temp disagree.
export function shiftAidenProfileTempF(profile, deltaF) {
  if (!profile || !deltaF) return profile;
  const dc = (deltaF * 5) / 9;
  const clampC = (c) => Math.max(50, Math.min(99, Math.round(c)));
  const shift = (arr) => (arr || []).map((t) => clampC(t + dc));
  return {
    ...profile,
    ssPulseTemperatures: shift(profile.ssPulseTemperatures),
    batchPulseTemperatures: shift(profile.batchPulseTemperatures),
    ...(profile.bloomEnabled ? { bloomTemperature: clampC((profile.bloomTemperature || 0) + dc) } : {}),
  };
}

// The morning-feedback vocabulary. These are the words Josh actually uses
// (docs/chatgpt-morning-coffee-transcript.md) and the ids double as keywords —
// suggestAdjustment does substring matching on them. Shared by BrewRating and
// MorningTweak so both touchpoints offer the same chips.
export const TASTE_DESCRIPTORS = [
  { id: "bitter", label: "Too bitter" },
  { id: "flat", label: "Flat / needs more flavor" },
  { id: "strong", label: "Too strong" },
  { id: "not sweet", label: "Not sweet enough" },
  { id: "stomach", label: "Hard on my stomach" },
];

// Move a grind setting by an Ode-native delta, remapped onto the caller's own
// grinder scale (lib/coffee-gear.js shape: {min, max, step}). The handoff's
// 0.2–0.4 corrections are Ode-native (a 1–11 dial, 10 wide); on a 1–40 grinder
// the same proportional correction is ~1 detent, and it must never be less than
// one detent or coarse-stepped dials would snap the move back to a no-op.
// Returns null when there is no number to move (descriptive-only grinder).
export function nudgeGrindSetting(setting, odeDelta, grinder = null) {
  if (setting == null || !Number.isFinite(+setting)) return null;
  const min = grinder?.min ?? 1;
  const max = grinder?.max ?? 11;
  const step = grinder?.step ?? 0.1;
  if (min == null || max == null) return null;
  const scaled = odeDelta * (max - min) / 10; // the Ode band is 10 wide
  const stepped = Math.sign(scaled) * Math.max(step, Math.abs(scaled));
  const snapped = Math.round((+setting + stepped) / step) * step;
  return +Math.min(max, Math.max(min, snapped)).toFixed(step < 1 ? 1 : 0);
}

// Dial-in feedback: given a previous brew + tasting feedback, suggest changes.
//
// Magnitudes come from docs/coffee-handoff.md, not folklore: the Ode moves in
// 0.1s and a real correction is 0.2–0.4 (the old ±1 whole click was an order of
// magnitude too big for a hot band only 0.3 wide); temperature moves ±1°C
// (~2°F). "More flavor" is fixed with extraction (finer/hotter), NEVER a
// tighter ratio — the only ratio move ever emitted is WEAKER (for "too
// strong"), applied via deltaRatio.
//
// `grinder` is the caller's scale ({min,max,step}); omitted means the Ode.
// Grind moves are emitted on THAT scale via nudgeGrindSetting — the previous
// version hardcoded the Ode's 1–11 clamp, which slammed a 1–40 grinder's
// setting to 11 (even inverting a coarser request into a huge finer move).
//
// When rules conflict on grind (e.g. "bitter and flat"), consumers take the
// LAST grind value — so the gentler, coarser-biased rules are ordered last and
// win, matching the standing smoother-never-stronger bias.
export function suggestAdjustment(previousBrew, feedback, grinder = null) {
  const f = (feedback || "").toLowerCase();
  const adjustments = [];
  const g = previousBrew.grind.setting;

  // Grind move on the caller's scale. Skipped entirely when there is no dial
  // (descriptive grinder) or the move clamps into a no-op at a bound.
  const pushGrind = (odeDelta, reason) => {
    const ns = nudgeGrindSetting(g, odeDelta, grinder);
    if (ns == null || ns === g) return;
    const direction = ns < g ? "finer" : "coarser";
    adjustments.push({
      lever: "grind", direction,
      change: `Grind ${direction} → ${ns}`,
      newSetting: ns,
      reason,
    });
  };
  const pushTemp = (deltaC, reason) => {
    adjustments.push({
      lever: "temp", direction: deltaC > 0 ? "up" : "down", deltaC,
      change: `${deltaC > 0 ? "Raise" : "Lower"} temp to ${previousBrew.temp_c + deltaC}°C`,
      reason,
    });
  };

  if (/sour|acidic|under/.test(f)) {
    pushGrind(-0.2, "Sour/underextracted — a touch finer extracts more.");
    pushTemp(1, "Slightly hotter boosts extraction.");
  }
  if (/flat|weak|thin|watery|hollow|muted|dull|more flavor|not enough flavor|no flavor/.test(f)) {
    pushGrind(-0.2, "More flavor through extraction — never more coffee.");
    pushTemp(1, "One degree hotter pulls more sweetness without harshness.");
  }
  // Complaint phrasings only ("not sweet enough", "want it sweeter", "more
  // chocolate") — praise like "nice sweetness" must not trigger a change.
  // Handoff: favor a finer grind before the dose, plus a slightly cooler brew.
  if (/not sweet|sweeter|more sweet|less sweet|more chocolate/.test(f)) {
    pushGrind(-0.1, "A touch finer pulls more sweetness — never more coffee.");
    pushTemp(-1, "Sweetness is balance, not max extraction — slightly cooler rounds it out.");
  }
  if (/strong|intense/.test(f)) {
    adjustments.push({
      lever: "ratio", direction: "weaker", deltaRatio: 0.5,
      change: "Ease the ratio a half step lighter",
      reason: "Less coffee for the same water — lighter, never stronger.",
    });
  }
  if (/bitter|over|astringent|harsh|burnt|smoky|ashy/.test(f)) {
    pushGrind(0.3, "Bitter/overextracted — coarser grind extracts less.");
    pushTemp(-1, "A degree cooler eases the bitter edge.");
  }
  if (/stomach|upset|heavy|rough/.test(f)) {
    pushGrind(0.3, "Easier on the stomach — lower extraction, gentler cup.");
    pushTemp(-1, "Cooler water keeps it gentle.");
  }
  if (/muddy|sludgy|fines/.test(f)) {
    pushGrind(0.3, "Reduce fines for a cleaner cup.");
  }

  return adjustments;
}

// Net temperature move from a set of adjustments, clamped to ±1°C — the handoff
// prescribes 1–2°F corrections, so stacked rules must not compound past that.
export function netTempDeltaC(adjustments) {
  const sum = (adjustments || []).reduce((acc, a) => acc + (a.lever === "temp" ? (a.deltaC ?? 0) : 0), 0);
  return Math.max(-1, Math.min(1, sum));
}

// Pre-planned next moves for a recipe card — the ritual's "If it tastes flat
// tomorrow → 5.3; if bitter → 5.9" line, computed up front so tomorrow's
// adjustment is already decided (docs/chatgpt-morning-coffee-transcript.md).
// Same magnitudes as suggestAdjustment; null values mean "no dial to move".
export function planNextMoves(brew, grinder = null) {
  const g = brew?.grind?.setting;
  const ifBitter = nudgeGrindSetting(g, 0.3, grinder);
  const ifFlat = nudgeGrindSetting(g, -0.2, grinder);
  return {
    ifBitter: ifBitter != null && ifBitter !== g ? ifBitter : null,
    ifFlat: ifFlat != null && ifFlat !== g ? ifFlat : null,
  };
}

// Cold-brew ready-by planning. The ritual: "It's 9:30pm and I want to drink it
// at 6am" → steep duration + compensation. Baseline (handoff): 12 h at the
// computed cold grind (~8.2 Ode) and 1:14 RTD. Shorter steeps compensate with a
// finer grind and a slightly tighter (still RTD) ratio — the transcript's
// 8.5 h overnight brew ran finer at 1:13. Very long steeps go a touch coarser
// so the cup doesn't turn woody. Durations snap to the Aiden's half-hour picker.
export function coldBrewPlan({ readyBy, now = new Date(), baseSetting, baseRatio = 14, grinder = null }) {
  if (!(readyBy instanceof Date) || !(now instanceof Date)) return null;
  const rawHours = (readyBy.getTime() - now.getTime()) / 3600000;
  if (!Number.isFinite(rawHours) || rawHours <= 0) return null;
  const durationHours = Math.min(24, Math.max(4, Math.round(rawHours * 2) / 2));

  let grindDelta = 0, ratio = baseRatio, note = null, warn = null;
  if (durationHours >= 14) {
    grindDelta = 0.3;
    note = "Long steep — a touch coarser so it doesn't go woody.";
  } else if (durationHours >= 12) {
    note = "Full steep at the baseline recipe.";
  } else if (durationHours >= 10) {
    grindDelta = -0.2;
    note = "Slightly finer to make up for the shorter steep.";
  } else if (durationHours >= 8) {
    grindDelta = -0.6; ratio = Math.min(ratio, 13.5);
    note = "Shorter steep — finer grind and a slightly tighter ratio compensate.";
  } else if (durationHours >= 6) {
    grindDelta = -0.9; ratio = Math.min(ratio, 13);
    note = "Short overnight steep — finer grind and 1:13 keep it from tasting thin.";
  } else {
    grindDelta = -1.2; ratio = Math.min(ratio, 13);
    warn = "Under 6 hours is a stretch for cold brew — expect a lighter cup.";
  }

  const grindSetting = baseSetting != null
    ? (nudgeGrindSetting(baseSetting, grindDelta, grinder) ?? baseSetting)
    : null;
  return { durationHours, grindSetting, ratio, note, warn };
}
